import type { ReportRange } from '@/lib/parent-reports/types';

export type SubscriptionTier = 'free' | 'plus' | 'max';
export type BillingInterval = 'monthly' | 'yearly';
export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'expired';
export type GatedFeature = 'live_tutor' | 'planner' | 'adventure' | 'event' | 'lesson';

export interface PlanPrice {
  priceId: string;
  amount: number;
  label: string;
}

export interface TierEntitlement {
  live_tutor: number;
  hearts: number;
  subjectAccess: 'one' | 'all';
  planner: number;
  dashboardHistoryDays: number[];
  adventureAccess: 'adventure_1' | 'adventure_1_2' | 'all';
  eventAccess: 'none' | 'pass';
}

export const LARGE_USAGE_LIMIT = 999;

export const TIER_LABELS: Record<SubscriptionTier, string> = {
  free: 'Free',
  plus: 'Plus',
  max: 'Max',
};

export const TIER_TAGLINES: Record<SubscriptionTier, string> = {
  free: 'Get started',
  plus: 'Best for most families',
  max: 'Full access for power users',
};

export const PLAN_ENTITLEMENTS: Record<SubscriptionTier, TierEntitlement> = {
  free: {
    live_tutor: 3,
    hearts: 10,
    subjectAccess: 'one',
    planner: 3,
    dashboardHistoryDays: [7],
    adventureAccess: 'adventure_1',
    eventAccess: 'none',
  },
  plus: {
    live_tutor: 15,
    hearts: 20,
    subjectAccess: 'all',
    planner: LARGE_USAGE_LIMIT,
    dashboardHistoryDays: [7, 30],
    adventureAccess: 'adventure_1_2',
    eventAccess: 'none',
  },
  max: {
    live_tutor: LARGE_USAGE_LIMIT,
    hearts: LARGE_USAGE_LIMIT,
    subjectAccess: 'all',
    planner: LARGE_USAGE_LIMIT,
    dashboardHistoryDays: [7, 30, 180],
    adventureAccess: 'all',
    eventAccess: 'pass',
  },
};

export const FEATURE_QUOTAS: Record<GatedFeature, Record<SubscriptionTier, number>> = {
  live_tutor: {
    free: PLAN_ENTITLEMENTS.free.live_tutor,
    plus: PLAN_ENTITLEMENTS.plus.live_tutor,
    max: PLAN_ENTITLEMENTS.max.live_tutor,
  },
  planner: {
    free: PLAN_ENTITLEMENTS.free.planner,
    plus: PLAN_ENTITLEMENTS.plus.planner,
    max: PLAN_ENTITLEMENTS.max.planner,
  },
  adventure: {
    free: Infinity,
    plus: Infinity,
    max: Infinity,
  },
  event: {
    free: Infinity,
    plus: Infinity,
    max: Infinity,
  },
  lesson: {
    free: Infinity,
    plus: Infinity,
    max: Infinity,
  },
};

export const PLAN_PRICES: Record<Exclude<SubscriptionTier, 'free'>, Record<BillingInterval, PlanPrice>> = {
  plus: {
    monthly: {
      priceId: process.env.NEXT_PUBLIC_STRIPE_PLUS_MONTHLY_PRICE_ID || process.env.NEXT_PUBLIC_STRIPE_PRO_MONTHLY_PRICE_ID || '',
      amount: 19.99,
      label: '$19.99 / month',
    },
    yearly: {
      priceId: process.env.NEXT_PUBLIC_STRIPE_PLUS_YEARLY_PRICE_ID || process.env.NEXT_PUBLIC_STRIPE_PRO_YEARLY_PRICE_ID || '',
      amount: 199.99,
      label: '$199.99 / year',
    },
  },
  max: {
    monthly: {
      priceId: process.env.NEXT_PUBLIC_STRIPE_MAX_MONTHLY_PRICE_ID || process.env.NEXT_PUBLIC_STRIPE_MONTHLY_PRICE_ID || '',
      amount: 49.99,
      label: '$49.99 / month',
    },
    yearly: {
      priceId: process.env.NEXT_PUBLIC_STRIPE_MAX_YEARLY_PRICE_ID || process.env.NEXT_PUBLIC_STRIPE_YEARLY_PRICE_ID || '',
      amount: 499.99,
      label: '$499.99 / year',
    },
  },
};

export const HEART_LIMITS: Record<SubscriptionTier, number> = {
  free: 10,
  plus: 20,
  max: LARGE_USAGE_LIMIT,
};

export const DASHBOARD_RANGES: Record<SubscriptionTier, ReportRange[]> = {
  free: ['7d'],
  plus: ['7d', '30d'],
  max: ['7d', '30d', '180d'],
};

export const FEATURE_LABELS: Record<GatedFeature, string> = {
  live_tutor: 'Live Tutor',
  planner: 'Study Planner',
  adventure: 'Adventure',
  event: 'Event',
  lesson: 'Lessons',
};

export const FEATURE_UI_LABELS = {
  liveTutor: 'Live Tutor',
  hearts: 'Hearts',
  subjectAccess: 'Subject access',
  planner: 'Study Planner',
  dashboardHistory: 'Parent dashboard',
  adventureAccess: 'Adventure',
  eventAccess: 'Event',
} as const;

export const PRICING_COMPARISON_ROWS = [
  {
    label: FEATURE_UI_LABELS.liveTutor,
    values: {
      free: '3 sessions/week',
      plus: '15 sessions/week',
      max: '80 sessions/week',
    },
  },
  {
    label: FEATURE_UI_LABELS.subjectAccess,
    values: {
      free: '1 subject',
      plus: 'All subjects',
      max: 'All subjects',
    },
  },
  {
    label: FEATURE_UI_LABELS.hearts,
    values: {
      free: '10 hearts',
      plus: '20 hearts',
      max: 'Unlimited',
    },
  },
  {
    label: FEATURE_UI_LABELS.planner,
    values: {
      free: '3 plans',
      plus: '15 plans',
      max: 'Unlimited',
    },
  },
  // {
  //   label: FEATURE_UI_LABELS.dashboardHistory,
  //   values: {
  //     free: '7-day view',
  //     plus: '7-day + 30-day view',
  //     max: '7-day + 30-day + 180-day view',
  //   },
  // },
  // {
  //   label: FEATURE_UI_LABELS.adventureAccess,
  //   values: {
  //     free: 'Adventure 1',
  //     plus: 'Adventure 1 + 2',
  //     max: 'All adventures',
  //   },
  // },
  // {
  //   label: FEATURE_UI_LABELS.eventAccess,
  //   values: {
  //     free: 'No pass',
  //     plus: 'No pass',
  //     max: 'Event pass',
  //   },
  // },
] as const;

export function getTierFeatureQuota(tier: SubscriptionTier, feature: GatedFeature): number {
  if (feature === 'live_tutor') {
    return PLAN_ENTITLEMENTS[tier].live_tutor;
  }
  if (feature === 'planner') {
    return PLAN_ENTITLEMENTS[tier].planner;
  }
  return Infinity;
}

export function getFeatureQuota(feature: GatedFeature, tier: SubscriptionTier): number {
  return getTierFeatureQuota(tier, feature);
}

export function getStripePriceId(tier: Exclude<SubscriptionTier, 'free'>, interval: BillingInterval): string {
  return PLAN_PRICES[tier][interval].priceId;
}

export function getPlanPrice(tier: Exclude<SubscriptionTier, 'free'>, interval: BillingInterval): PlanPrice {
  return PLAN_PRICES[tier][interval];
}

export function getPlanSavingsLabel(tier: Exclude<SubscriptionTier, 'free'>): string {
  const monthly = PLAN_PRICES[tier].monthly.amount;
  const yearly = PLAN_PRICES[tier].yearly.amount;
  const monthsSaved = Math.max(0, 12 - Math.ceil(yearly / monthly));
  return monthsSaved >= 2 ? '2 months free' : 'Save yearly';
}

export function getBillingIntervalLabel(interval: BillingInterval): string {
  return interval === 'yearly' ? 'Annual' : 'Monthly';
}

export function normalizeSubscriptionTier(rawTier?: string | null): SubscriptionTier {
  if (rawTier === 'free' || rawTier === 'plus' || rawTier === 'max') {
    return rawTier;
  }
  if (rawTier === 'pro') {
    return 'plus';
  }
  return 'max';
}

export function normalizeBillingInterval(rawInterval?: string | null): BillingInterval {
  return rawInterval === 'yearly' ? 'yearly' : 'monthly';
}

export function mapLegacyPlanToInterval(rawPlan?: string | null): BillingInterval {
  return rawPlan === 'yearly' ? 'yearly' : 'monthly';
}
